import React, { FC, useMemo } from "react";
import cn from "classnames";
import stl from "./index.module.scss";
import { useStore } from "effector-react";
import { useTranslation } from "react-i18next";
import format from "date-fns/format";
import uniq from "lodash/uniq";
import { $selectedLocation } from "../../store/selected/selectedLocations";
import { $selectedPersonTypes } from "../../store/selected/selectedPersonTypes";
import {
  $selectedPackages,
  selectedPackagesApi,
} from "../../store/selected/selectedPackages";
import {
  $selectedActivities,
  selectedActivitiesApi,
} from "../../store/selected/selectedActivities";
import { $currentLocale } from "../../store/shared/locales";
import { fillStr, humanizeMinutes, isPresent } from "../../utils";
import {
  $prepayment,
  $subTotalCost,
  $totalCost,
  $totalTaxes,
} from "../../store/selected/shared";
import {
  $selectedSupplements,
  selectedSupplementsApi,
} from "../../store/selected/selectedSupplements";
import { $currentStep } from "../../steps/stepsStore";
import { mainStep } from "../../steps/Main";
import { menuStep } from "../../steps/Menu";
import { $countdown } from "../../store/shared/countdown";
import { AppConfig } from "../../config";
import { Money } from "../../components";

const BookingDetails: FC<{ styleTopOpen?: boolean }> = ({ styleTopOpen }) => {
  return (
    <div className={cn(stl.block, { [stl.block_firstTopOpen]: styleTopOpen })}>
      <DateRender />
      <LocationRender />
      <PersonTypesRender />
      <PackagesRender />
      <ActivitiesRender />
      <SupplementsRender />
      <SubtotalRender />
      <TotalRender />
    </div>
  );
};

export default BookingDetails;

export const CountdownRender: FC = () => {
  const { t } = useTranslation(AppConfig.translationFileNames.base);
  const countdown = useStore($countdown);

  const { seconds } = countdown;

  const secondsRemaining = seconds % 60;
  const minutesRemaining = parseInt(seconds / 60 + "");

  const timeRemainingStr = `${minutesRemaining}:${fillStr(secondsRemaining)}`;

  return (
    <>
      {t("aside.timeReserved")}:{" "}
      <time style={{ minWidth: timeRemainingStr.length * 0.5 + 0.2 + "em" }}>
        {timeRemainingStr}
      </time>{" "}
      <span>{t("aside.minutes")}</span>
    </>
  );
};

const LocationRender: FC = () => {
  const data = useStore($selectedLocation);

  if (!data) return null;

  return (
    <section>
      <h3>{data.name}</h3>
      <p>{data.address}</p>
    </section>
  );
};

const DateRender: FC = () => {
  const { t } = useTranslation(AppConfig.translationFileNames.base);
  const { dateFnsLocale: locale } = useStore($currentLocale);
  const packages = useStore($selectedPackages);
  const activities = useStore($selectedActivities);

  const dates = useMemo<string[] | null>(() => {
    if (packages.length === 0 && activities.length === 0) return null;

    const formatDate = (v: Date) => format(v, "d MMMM", { locale });

    const dates: string[] = [];

    packages.forEach((package_) => {
      dates.push(formatDate(package_.availabilitySlot.date));
    });
    activities.forEach((activity) => {
      dates.push(formatDate(activity.availabilitySlot.date));
    });

    return uniq(dates);
  }, [packages, activities, locale]);

  if (!dates) return null;

  return (
    <section>
      <h3>{t("aside.date")}</h3>
      <p>{dates?.join(", ")}</p>
    </section>
  );
};

const PersonTypesRender: FC = () => {
  const { t } = useTranslation(AppConfig.translationFileNames.base);
  const data = useStore($selectedPersonTypes);

  if (data.length === 0) return null;

  return (
    <section>
      <h3>{t("aside.personTypes")}</h3>
      {data.map(({ name, quantity }) => (
        <p key={name}>
          {quantity} x {name}
        </p>
      ))}
    </section>
  );
};

const PackagesRender: FC = () => {
  const { t } = useTranslation(AppConfig.translationFileNames.base);
  const { dateFnsLocale: locale } = useStore($currentLocale);
  const data = useStore($selectedPackages);
  const currentStep = useStore($currentStep);

  if (data.length === 0) return null;

  return (
    <section>
      <h3>{t("aside.packages")}</h3>
      {data.map((package_) => {
        const {
          entity: { name },
          guestsQuantity,
          capacityPackageInstancesQuantity,
          availabilitySlot,
        } = package_;

        const dateStr = format(availabilitySlot.date, "dd MMM HH:mm", {
          locale,
        });

        return (
          <div key={name} className={stl.product}>
            {mainStep.order === currentStep && (
              <button
                className={stl.remove}
                onClick={() => {
                  selectedPackagesApi.remove({ id: package_.entity.id });
                }}
              />
            )}

            <div className={stl.tableWrap}>
              <table>
                <tbody>
                  <tr>
                    <td>
                      <time>{dateStr}</time>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <i>
                        {capacityPackageInstancesQuantity || guestsQuantity} x
                      </i>{" "}
                      {name}
                    </td>
                    <td>
                      <Money value={availabilitySlot.totalPrice} />
                    </td>
                  </tr>
                  <tr>
                    <td>{t("aside.vat")}</td>
                    <td>
                      <sub>{t("aside.included")}</sub>
                      <Money value={availabilitySlot.totalTax} />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </section>
  );
};

const ActivitiesRender: FC = () => {
  const { t } = useTranslation(AppConfig.translationFileNames.base);
  const { dateFnsLocale: locale } = useStore($currentLocale);
  const data = useStore($selectedActivities);
  const currentStep = useStore($currentStep);

  if (data.length === 0) return null;

  return (
    <section>
      <h3>{t("aside.activities")}</h3>
      {data.map((activity) => {
        const {
          entity: { name },
          timeSlots,
          slotDuration,
          availabilitySlot,
        } = activity;

        const dateStr = format(availabilitySlot.date, "dd MMM HH:mm", {
          locale,
        });

        const duration = humanizeMinutes(timeSlots * slotDuration, locale);

        return (
          <div key={name} className={stl.product}>
            {mainStep.order === currentStep && (
              <button
                className={stl.remove}
                onClick={() => {
                  selectedActivitiesApi.remove({ id: activity.entity.id });
                }}
              />
            )}
            <div className={stl.tableWrap}>
              <table>
                <tbody>
                  <tr>
                    <td>
                      <time>{dateStr}</time>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      {name} <br /> <i>{duration}</i>
                    </td>
                    <td>
                      <Money value={availabilitySlot.totalPrice} />
                    </td>
                  </tr>
                  <tr>
                    <td>{t("aside.vat")}</td>
                    <td>
                      <sub>{t("aside.included")}</sub>
                      <Money value={activity.availabilitySlot.totalTax} />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </section>
  );
};

const SupplementsRender: FC = () => {
  const { t } = useTranslation(AppConfig.translationFileNames.base);
  const data = useStore($selectedSupplements);
  const currentStep = useStore($currentStep);

  if (data.length === 0) return null;

  return (
    <section>
      <h3>{t("aside.supplements")}</h3>
      {data.map((supplement) => {
        const {
          entity: { name },
          quantity,
        } = supplement;

        return (
          <div key={name + supplement.entity.id} className={stl.product}>
            {menuStep.order === currentStep && (
              <button
                className={stl.remove}
                onClick={() => {
                  selectedSupplementsApi.remove({
                    id: supplement.entity.id,
                  });
                }}
              />
            )}
            <div className={stl.tableWrap}>
              <table>
                <tbody>
                  <tr>
                    <td>
                      <i>{quantity} x</i> {name}
                    </td>
                    <td>
                      <Money value={supplement.totalPrice} />
                    </td>
                  </tr>
                  <tr>
                    <td>{t("aside.tax")}</td>
                    <td style={{ textAlign: "right" }}>
                      <sub>{t("aside.included")}</sub>
                      <Money value={supplement.totalTax} />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </section>
  );
};

const SubtotalRender: FC = () => {
  const { t } = useTranslation(AppConfig.translationFileNames.base);
  const subTotalCost = useStore($subTotalCost);
  const totalTaxes = useStore($totalTaxes);

  return (
    <section>
      <div className={stl.tableWrap}>
        <table style={{ width: "100%" }}>
          <tbody>
            {isPresent(subTotalCost) && (
              <tr>
                <td>
                  <h3>{t("aside.subtotal")}</h3>
                </td>
                <td style={{ textAlign: "right" }}>
                  <h3>
                    <Money value={subTotalCost} />
                  </h3>
                </td>
              </tr>
            )}

            {isPresent(totalTaxes) && (
              <tr>
                <td>{t("aside.tax")}</td>
                <td style={{ textAlign: "right" }}>
                  <Money value={totalTaxes} />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
};

const TotalRender: FC = () => {
  const { t } = useTranslation(AppConfig.translationFileNames.base);
  const { dateFnsLocale: locale } = useStore($currentLocale);
  const prepayment = useStore($prepayment);
  const totalCost = useStore($totalCost);

  const prepaymentDate = useMemo<string | null>(() => {
    if (!prepayment?.date) return null;
    return format(prepayment.date, "dd-MM", { locale });
  }, [prepayment, locale]);

  if (!isPresent(totalCost)) return null;

  return (
    <section>
      <div className={stl.tableWrap}>
        <table style={{ width: "100%" }}>
          <tbody>
            {isPresent(totalCost) && (
              <tr>
                <td>
                  <h3>{t("aside.total")}</h3>
                </td>
                <td style={{ textAlign: "right" }}>
                  <h3>
                    <Money value={totalCost} />
                  </h3>
                </td>
              </tr>
            )}

            {prepayment && (
              <tr>
                <td>{t("aside.prepaid")}</td>
                <td style={{ textAlign: "right", color: "var(--c-black)" }}>
                  <Money value={prepayment.amount} />
                </td>
              </tr>
            )}
            {prepaymentDate && (
              <tr>
                <td>{t("aside.dueDate")}</td>
                <td style={{ textAlign: "right", color: "var(--c-black)" }}>
                  {prepaymentDate}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
};
